/**
 * Application Shell Layout (SCR-000)
 *
 * @requirement BRD-000 Foundational Screens
 * @user-story US-000 Application Shell
 * @screen SCR-000 Application Shell
 * @iso27001 A.9.4.1 Information access restriction
 */

import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { usePermissions } from '@myvault/shared/hooks';
import { cn } from '@myvault/shared/utils';

import { Header } from '../components/shell/Header';
import { Sidebar } from '../components/shell/Sidebar';
import { MobileNav } from '../components/shell/MobileNav';
import { MobileMenuDrawer } from '../components/shell/MobileMenuDrawer';
import { NotificationsPanel } from '../components/shell/NotificationsPanel';
import { useNotifications } from '../hooks/useNotifications';
import { PersonaIndicator } from '../features/dev/components';
import { useDevKeyboardShortcuts } from '../features/dev/hooks';
import { IssueFAB } from '../features/dev/issue-fab';

export function AppLayout() {
  const location = useLocation();
  const { hasPersona } = usePermissions();
  const { unreadCount } = useNotifications();

  // Development keyboard shortcuts (BRD-DEV-001 US-DEV-006)
  useDevKeyboardShortcuts();

  // Shell state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem('sidebar_collapsed');
    return stored === 'true';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationsPanelOpen, setNotificationsPanelOpen] = useState(false);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close panels on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setNotificationsPanelOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Skip Links for Accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded focus:bg-blue-600 focus:px-4 focus:py-2 focus:text-white"
      >
        Ga naar hoofdinhoud
      </a>
      <a
        href="#main-nav"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-16 focus:z-[100] focus:rounded focus:bg-blue-600 focus:px-4 focus:py-2 focus:text-white"
      >
        Ga naar navigatie
      </a>

      {/* Header */}
      <Header
        onMenuToggle={() => setMobileMenuOpen(true)}
        onNotificationsToggle={() => setNotificationsPanelOpen(!notificationsPanelOpen)}
        notificationCount={unreadCount}
      />

      <div className="flex">
        {/* Sidebar (tablet/desktop) */}
        <Sidebar
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
          currentPath={location.pathname}
          hasPersona={hasPersona}
        />

        {/* Main Content */}
        <main
          id="main-content"
          className={cn(
            'flex-1 transition-all duration-200',
            // GAP-003: Adjust margin for sidebar
            // Tablet (md to lg): Always 16 (navigation rail)
            // Desktop (lg+): 64 expanded or 16 collapsed
            'md:ml-16 lg:ml-64',
            sidebarCollapsed && 'lg:ml-16',
            // Padding
            'p-4 sm:p-6 lg:p-8',
            // GAP-006: Account for taller mobile bottom nav (68px + safe area)
            // Removed on tablet since we have sidebar
            'pb-24 md:pb-6 lg:pb-8'
          )}
        >
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <MobileNav currentPath={location.pathname} />

      {/* Mobile Menu Drawer */}
      <MobileMenuDrawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        currentPath={location.pathname}
        hasPersona={hasPersona}
      />

      {/* Notifications Panel */}
      <NotificationsPanel
        open={notificationsPanelOpen}
        onClose={() => setNotificationsPanelOpen(false)}
      />

      {/* Development Persona Indicator (BRD-DEV-001) */}
      <PersonaIndicator />

      {/* Development Issue FAB (BRD-DEV-001) */}
      <IssueFAB bottomOffset={96} />

      {/* Footer (desktop only) */}
      <footer className="hidden border-t border-gray-200 bg-white py-4 lg:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-8 text-sm text-gray-500">
          <p>&copy; 2025 myVault. Alle rechten voorbehouden.</p>
          <div className="flex gap-6">
            <Link to="/legal/privacy" className="hover:text-blue-600">
              Privacybeleid
            </Link>
            <Link to="/legal/terms" className="hover:text-blue-600">
              Algemene voorwaarden
            </Link>
            <Link to="/help/contact" className="hover:text-blue-600">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
